import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LoaderCircle, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DateRangePicker,
  Modal,
  ModalBody,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { ANALYTICS_QUERY_KEYS, generateAdhocReport, summaryOf } from './analyticsApi';
import type { AnalyticsReportArchive } from './analyticsTypes';
import { ANALYTICS_FRESHNESS, type AnalyticsFreshness } from './analyticsState';

const BLOCKED_REASON_KEYS = {
  [ANALYTICS_FRESHNESS.stale]: 'analytics.generate.blockedStale',
  [ANALYTICS_FRESHNESS.unavailable]: 'analytics.generate.blockedUnavailable',
} as const;

/** Why generation cannot run now, or `null` when it can. */
function blockedReasonKey(
  freshness: AnalyticsFreshness | undefined,
  period: readonly [string, string],
): string | null {
  if (freshness === undefined) {
    return BLOCKED_REASON_KEYS[ANALYTICS_FRESHNESS.unavailable];
  }
  if (freshness.kind !== ANALYTICS_FRESHNESS.current) {
    return BLOCKED_REASON_KEYS[freshness.kind];
  }
  if (period[0] === '' || period[1] === '' || period[0] > period[1]) {
    return 'analytics.generate.blockedRange';
  }
  return null;
}

export function GenerateReportDialog({
  freshness,
  onGenerated,
}: {
  freshness: AnalyticsFreshness | undefined;
  onGenerated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState<[string, string]>(['', '']);
  const generate = useMutation({
    mutationFn: ([periodStart, periodEnd]: [string, string]) =>
      generateAdhocReport(periodStart, periodEnd),
    onSuccess: (created) => {
      queryClient.setQueryData(ANALYTICS_QUERY_KEYS.detail(created.id), created);
      // Insert the new row before selecting it, so the picker has its label
      // while the archive refetch is still in flight.
      queryClient.setQueryData<AnalyticsReportArchive>(ANALYTICS_QUERY_KEYS.archive, (archive) =>
        archive === undefined || archive.items.some((item) => item.id === created.id)
          ? archive
          : { ...archive, items: [...archive.items, summaryOf(created)] },
      );
      void queryClient.invalidateQueries({ queryKey: ANALYTICS_QUERY_KEYS.archive, exact: true });
      onGenerated(created.id);
      setOpen(false);
      setPeriod(['', '']);
      toast.success(t('analytics.generate.successTitle'), t('analytics.generate.successMessage'));
    },
    onError: () => {
      toast.error(t('analytics.generate.errorTitle'), t('analytics.generate.errorMessage'));
    },
  });
  const blocked = blockedReasonKey(freshness, period);

  function close() {
    if (!generate.isPending) {
      setOpen(false);
    }
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (blocked === null) {
      generate.mutate(period);
    }
  }

  return (
    <>
      <Button type="button" size="sm" onClick={() => setOpen(true)}>
        <Plus data-icon="inline-start" />
        {t('analytics.generate.title')}
      </Button>
      <Modal isOpen={open} onClose={close} size="md">
        <form onSubmit={submit}>
          <ModalHeader>
            <ModalTitle>{t('analytics.generate.title')}</ModalTitle>
            <ModalDescription>{t('analytics.generate.description')}</ModalDescription>
          </ModalHeader>
          <ModalBody className="flex flex-col gap-3">
            <DateRangePicker
              value={period}
              onChange={setPeriod}
              placeholder={t('analytics.generate.selectRange')}
              className="w-full"
            />
            {blocked !== null && (
              <p className="text-xs text-muted-foreground" role="status">
                {t(blocked)}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button type="button" variant="ghost" onClick={close} disabled={generate.isPending}>
              {t('ui.cancel')}
            </Button>
            <Button type="submit" disabled={blocked !== null || generate.isPending}>
              {generate.isPending && (
                <LoaderCircle data-icon="inline-start" className="animate-spin" />
              )}
              {generate.isPending
                ? t('analytics.generate.runningShort')
                : t('analytics.generate.button')}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </>
  );
}
